import React, { useEffect } from "react";
import {
  FaRegClock,
  FaRegCalendarAlt,
  FaUserEdit,
  FaGraduationCap,
  FaArrowRight,
  FaBookReader,
} from "react-icons/fa";
import { getPostBySlug } from "../data/blogPosts";
import { applySeo, SITE_URL } from "../utils/seo";
import "./Blog.css";

// Başlıklardan anchor id üret (içerik bloğu render'ı için)
const slugifyHeading = (text) =>
  text
    .toLowerCase()
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function renderBlock(block, index, onBook, onOpenPost) {
  switch (block.type) {
    case "h2":
      return (
        <h2 key={index} id={slugifyHeading(block.text)}>
          {block.text}
        </h2>
      );
    case "h3":
      return <h3 key={index}>{block.text}</h3>;
    case "p":
      return <p key={index}>{block.text}</p>;
    case "ul":
      return (
        <ul key={index}>
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={index}>
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote key={index} className="blog-quote">
          {block.text}
        </blockquote>
      );
    case "cta":
      return (
        <div key={index} className="blog-inline-cta">
          <p>{block.text}</p>
          <button className="blog-cta-btn" onClick={onBook}>
            <FaGraduationCap /> Görüşme Randevusu Al
          </button>
        </div>
      );
    case "related": {
      const target = getPostBySlug(block.slug);
      if (!target) return null;
      return (
        <a
          key={index}
          className="blog-related"
          href={`/blog/${target.slug}`}
          onClick={(e) => {
            e.preventDefault();
            if (onOpenPost) onOpenPost(target.slug);
          }}
        >
          <FaBookReader className="blog-related-icon" />
          <span className="blog-related-text">
            <small>İlgili rehber</small>
            {block.label || target.title}
          </span>
          <FaArrowRight className="blog-related-arrow" />
        </a>
      );
    }
    default:
      return null;
  }
}

function BlogPost({ slug, onOpenList, onOpenPost, onBook, onHome }) {
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) {
      const cleanup = applySeo({
        title: "Yazı Bulunamadı | Almanya Kampüs Blog",
        description: "Aradığınız blog yazısı bulunamadı.",
        canonical: SITE_URL + "/blog",
      });
      window.scrollTo(0, 0);
      return cleanup;
    }

    const url = `${SITE_URL}/blog/${post.slug}`;
    const imageUrl = SITE_URL + post.cover;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      headline: post.title,
      description: post.description,
      image: imageUrl,
      author: { "@type": "Organization", name: post.author },
      publisher: {
        "@type": "Organization",
        name: "Almanya Kampüs",
        logo: { "@type": "ImageObject", url: SITE_URL + "/logo.png" },
      },
      datePublished: post.date,
      dateModified: post.date,
      keywords: post.keywords,
      inLanguage: "tr-TR",
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: SITE_URL + "/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    };

    const graph = [articleSchema, breadcrumbSchema];

    if (post.faq && post.faq.length) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    const cleanup = applySeo({
      title: `${post.title} | Almanya Kampüs`,
      description: post.description,
      canonical: url,
      image: imageUrl,
      keywords: post.keywords,
      type: "article",
      jsonLd: graph,
    });
    window.scrollTo(0, 0);
    return cleanup;
  }, [post]);

  if (!post) {
    return (
      <div className="blog-page">
        <div className="blog-topbar">
          <button className="blog-brand" onClick={onHome}>
            <img src="/logo.png" alt="Almanya Kampüs" />
            <span>Almanya Kampüs</span>
          </button>
          <button className="blog-book-btn" onClick={onBook}>
            <FaGraduationCap /> Ücretsiz Görüşme Al
          </button>
        </div>
        <div className="blog-notfound">
          <h1>Yazı bulunamadı</h1>
          <p>Aradığınız blog yazısı taşınmış veya kaldırılmış olabilir.</p>
          <button className="blog-cta-btn" onClick={onOpenList}>
            <FaArrowRight /> Tüm Yazılara Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <div className="blog-topbar">
        <button className="blog-brand" onClick={onHome}>
          <img src="/logo.png" alt="Almanya Kampüs" />
          <span>Almanya Kampüs</span>
        </button>
        <button className="blog-book-btn" onClick={onBook}>
          <FaGraduationCap /> Ücretsiz Görüşme Al
        </button>
      </div>

      <nav className="blog-breadcrumb" aria-label="breadcrumb">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            onHome();
          }}
        >
          Ana Sayfa
        </a>{" "}
        /{" "}
        <a
          href="/blog"
          onClick={(e) => {
            e.preventDefault();
            onOpenList();
          }}
        >
          Blog
        </a>{" "}
        / <span>{post.category}</span>
      </nav>

      <article className="blog-article">
        <header className="blog-article-header">
          <span className="blog-article-cat">{post.category}</span>
          <h1>{post.title}</h1>
          <div className="blog-article-meta">
            <span>
              <FaUserEdit /> {post.author}
            </span>
            <span>
              <FaRegCalendarAlt /> {post.dateReadable}
            </span>
            <span>
              <FaRegClock /> {post.readingTime} dk okuma
            </span>
          </div>
        </header>

        <img
          className="blog-article-cover"
          src={post.cover}
          alt={post.coverAlt}
        />

        <div className="blog-content">
          {post.content.map((block, i) =>
            renderBlock(block, i, onBook, onOpenPost)
          )}
        </div>

        {post.tags && post.tags.length > 0 && (
          <div className="blog-tags">
            {post.tags.map((t) => (
              <span key={t} className="tag">
                #{t}
              </span>
            ))}
          </div>
        )}

        {post.faq && post.faq.length > 0 && (
          <section className="blog-faq">
            <h2>Sıkça Sorulan Sorular</h2>
            {post.faq.map((f, i) => (
              <div className="blog-faq-item" key={i}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </section>
        )}

        <section className="blog-final-cta">
          <h2>Almanya'da Ücretsiz Eğitim Hayalinize Bugün Başlayın</h2>
          <p>
            Sıfır Almanca ile başvuru sürecinizi uzman danışmanlarımızla
            ücretsiz planlayalım. Size özel yol haritanızı çıkaralım.
          </p>
          <button className="blog-cta-btn" onClick={onBook}>
            <FaGraduationCap /> Ücretsiz Görüşme Randevusu Al
          </button>
        </section>
      </article>
    </div>
  );
}

export default BlogPost;
